<template>
  <el-form
    ref="form"
    :model="formValue"
    :rules="formRules"
    @status="handleFormStatusChange"
  >
    <el-row :gutter="24">
      <el-col
        :span="isExtended ? 12 : 24"
      >
        <el-form-item prop="officialName">
          <el-input
            v-model="formValue.officialName"
            :label="$t('profile.company.official-name')"
            required
            maxlength="255"
            data-test-id="company-form.input-official-name"
          />
        </el-form-item>
      </el-col>
      <el-col
        v-if="isExtended"
        :span="12"
      >
        <el-form-item prop="brandName">
          <el-input
            v-model="formValue.brandName"
            maxlength="255"
            :label="$t('profile.company.brand-name')"
            data-test-id="company-form.input-brand-name"
          />
        </el-form-item>
      </el-col>
    </el-row>
    <template v-if="isExtended">
      <el-row :gutter="24">
        <el-col :span="12">
          <el-form-item prop="registrationNumber">
            <el-input
              v-model="formValue.registrationNumber"
              maxlength="50"
              :label="$t('profile.company.registration-number')"
              data-test-id="company-form.input-registration-number"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="24">
        <el-col :span="12">
          <el-form-item prop="size">
            <el-select
              v-model="formValue.size"
              :label="$t('profile.company.size')"
              data-test-id="company-form.select-company-size"
              @change="handleLegalContactChange"
            >
              <el-option
                v-for="size in sizes"
                :key="size"
                :label="size"
                :value="size"
                :data-test-id="`company-form.select-company-size.item-${size}`"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item prop="website">
            <el-input
              v-model="formValue.website"
              :label="$t('profile.company.website')"
              maxlength="255"
              prefix-value="https://"
              data-test-id="company-form.input-website"
              @change="handleLegalContactChange"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="24">
        <el-col :span="12">
          <el-form-item prop="industry">
            <el-select
              v-model="formValue.industry"
              :label="$t('profile.company.industry')"
              data-test-id="company-form.select-industry"
              @change="handleLegalContactChange"
            >
              <el-option
                v-for="industry in industries"
                :key="industry"
                :label="industry"
                :value="industry"
                :data-test-id="`company-form.select-industry.item-${industry}`"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
    </template>
    <div
      class="el-text el-text--body-24 el-text--weight-bold mb-24"
      data-test-id="company-form.legal-address"
    >
      {{ $t('profile.company.legal-address') }}
    </div>
    <el-row :gutter="24">
      <el-col :span="countryHasStates(formValue.country) ? 12 : 24">
        <el-form-item prop="country">
          <el-select
            v-model="formValue.country"
            :label="$t('profile.company.country')"
            filterable
            required
            data-test-id="company-form.select-country"
            @change="handleCountryChange(formValue); handleLegalContactChange()"
          >
            <el-option
              v-for="country in countries"
              :key="country.code"
              :value="country.code"
              :label="country.name"
              :data-test-id="`company-form.select-country.item-${country.code}`"
            />
          </el-select>
        </el-form-item>
      </el-col>
      <el-col
        v-if="countryHasStates(formValue.country)"
        :span="12"
      >
        <el-form-item prop="state">
          <el-select
            v-model="formValue.state"
            :label="$t('profile.company.state')"
            filterable
            required
            data-test-id="company-form.select-state"
            @change="handleLegalContactChange"
          >
            <el-option
              v-for="state in states(formValue.country)"
              :key="state"
              :label="state"
              :value="state"
              :data-test-id="`company-form.select-state.item-${state}`"
            />
          </el-select>
        </el-form-item>
      </el-col>
    </el-row>
    <el-row :gutter="24">
      <el-col
        v-if="isExtended"
        :span="12"
      >
        <el-form-item prop="city">
          <el-input
            v-model="formValue.city"
            maxlength="64"
            :label="$t('profile.company.city')"
            data-test-id="company-form.input-city"
            @change="handleLegalContactChange"
          />
        </el-form-item>
      </el-col>
      <el-col :span="isExtended ? 12 : 24">
        <el-form-item prop="zipCode">
          <el-input
            v-model="formValue.zipCode"
            maxlength="13"
            :label="$t('profile.company.zip-code')"
            data-test-id="company-form.input-zip-code"
            @change="handleLegalContactChange"
          />
        </el-form-item>
      </el-col>
    </el-row>
    <template v-if="isExtended">
      <el-row :gutter="24">
        <el-col :span="24">
          <el-form-item prop="address">
            <el-input
              v-model="formValue.address"
              maxlength="150"
              :label="$t('profile.company.address')"
              data-test-id="company-form.input-address"
              @change="handleLegalContactChange"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <div
        class="el-text el-text--body-24 el-text--weight-bold mb-24"
        data-test-id="company-form.primary-contact"
      >
        {{ $t('profile.company.primary-contact') }}
      </div>
      <el-row :gutter="24">
        <el-col :span="12">
          <el-form-item prop="legalContact.firstName">
            <el-input
              v-model="formValue.legalContact.firstName"
              :label="$t('profile.contact-list.first-name')"
              required
              maxlength="100"
              data-test-id="company-form.input-first-name"
              @change="handleLegalContactChange"
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item prop="legalContact.lastName">
            <el-input
              v-model="formValue.legalContact.lastName"
              :label="$t('profile.contact-list.last-name')"
              required
              maxlength="100"
              data-test-id="company-form.input-last-name"
              @change="handleLegalContactChange"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="24">
        <el-col :span="12">
          <el-form-item prop="legalContact.email">
            <el-input
              v-model="formValue.legalContact.email"
              :label="$t('profile.company.email')"
              required
              maxlength="255"
              data-test-id="company-form.input-email"
              @change="handleLegalContactChange"
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item prop="legalContact.phone">
            <el-input
              v-model="formValue.legalContact.phone"
              :label="$t('profile.company.phone')"
              prefix-value="+"
              maxlength="22"
              data-test-id="company-form.input-phone"
              @change="handleLegalContactChange"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <div
        class="el-text el-text--body-24 el-text--weight-bold mb-8"
        data-test-id="company-form.billing-address"
      >
        {{ $t('profile.company.billing-address') }}
      </div>
      <el-checkbox
        v-model="isBillingSameAsLegal"
        class="mb-16"
        data-test-id="company-form.checkbox-billing-same-as-legal"
        @change="alignBillingContactToLegal"
      >
        {{ $t('profile.company.same-as-legal-information') }}
      </el-checkbox>
      <template v-if="!isBillingSameAsLegal">
        <el-row :gutter="24">
          <el-col :span="countryHasStates(formValue.billingContact.country) ? 12 : 24">
            <el-form-item prop="billingContact.country">
              <el-select
                v-model="formValue.billingContact.country"
                :label="$t('profile.company.country')"
                filterable
                required
                data-test-id="company-form.select-country"
                @change="handleCountryChange(formValue.billingContact)"
              >
                <el-option
                  v-for="country in countries"
                  :key="country.code"
                  :value="country.code"
                  :label="country.name"
                  :data-test-id="`company-form.select-country.item-${country.code}`"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col
            v-if="countryHasStates(formValue.billingContact.country)"
            :span="12"
          >
            <el-form-item prop="billingContact.state">
              <el-select
                v-model="formValue.billingContact.state"
                :label="$t('profile.company.state')"
                filterable
                required
                data-test-id="company-form.select-state"
              >
                <el-option
                  v-for="state in states(formValue.billingContact.country)"
                  :key="state"
                  :label="state"
                  :value="state"
                  :data-test-id="`company-form.select-state.item-${state}`"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="24">
          <el-col :span="12">
            <el-form-item prop="billingContact.city">
              <el-input
                v-model="formValue.billingContact.city"
                maxlength="64"
                :label="$t('profile.company.city')"
                data-test-id="company-form.input-city"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item prop="billingContact.zipCode">
              <el-input
                v-model="formValue.billingContact.zipCode"
                maxlength="13"
                :label="$t('profile.company.zip-code')"
                data-test-id="company-form.input-zip-code"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="24">
          <el-col :span="24">
            <el-form-item prop="billingContact.address">
              <el-input
                v-model="formValue.billingContact.address"
                maxlength="150"
                :label="$t('profile.company.address')"
                data-test-id="company-form.input-address"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <div
          class="el-text el-text--body-24 el-text--weight-bold mb-24"
          data-test-id="company-form.primary-billing-contact"
        >
          {{ $t('profile.company.primary-billing-contact') }}
        </div>
        <el-row :gutter="24">
          <el-col :span="12">
            <el-form-item prop="billingContact.firstName">
              <el-input
                v-model="formValue.billingContact.firstName"
                :label="$t('profile.contact-list.first-name')"
                required
                maxlength="100"
                data-test-id="company-form.input-first-name"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item prop="billingContact.lastName">
              <el-input
                v-model="formValue.billingContact.lastName"
                :label="$t('profile.contact-list.last-name')"
                required
                maxlength="100"
                data-test-id="company-form.input-last-name"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="24">
          <el-col :span="12">
            <el-form-item prop="billingContact.email">
              <el-input
                v-model="formValue.billingContact.email"
                :label="$t('profile.company.email')"
                required
                maxlength="255"
                data-test-id="company-form.input-email"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item prop="billingContact.phone">
              <el-input
                v-model="formValue.billingContact.phone"
                :label="$t('profile.company.phone')"
                prefix-value="+"
                maxlength="22"
                data-test-id="company-form.input-phone"
              />
            </el-form-item>
          </el-col>
        </el-row>
      </template>
    </template>
  </el-form>
</template>

<script>
import { CompanySize, CompanyIndustry, getDefaultCompanyFormValue } from '@/app/domain/Company';
import { alignContactInfo, isContactInfoEqual, updateContactFromCompany } from '@/app/domain/Contact';
import { getCountries, getStates, hasStates } from '@/app/library/country-state-picker';
import { isEmailValid } from '@/app/library/isEmailValid';
import { isEmpty } from '@/app/library/isEmpty';

export default {
  name: 'CompanyForm',
  props: {
    value: {
      type: Object,
      default: () => ({})
    },
    isExtended: {
      type: Boolean,
      default: false
    }
  },
  emits: [
    'input',
    'status'
  ],
  data() {
    const addressFormRules = {
      country: [
        {
          required: true,
          message: this.$t('form-error.enter', { field: this.$t('profile.company.country').toLowerCase() })
        }
      ],
      state: [
        {
          required: true,
          message: this.$t('form-error.enter', { field: this.$t('profile.company.state').toLowerCase() })
        }
      ],
      zipCode: [
        {
          validator: this.validateZipCode
        }
      ]
    };

    const contactFormRules = {
      firstName: [
        {
          required: true,
          message: this.$t('form-error.enter', { field: this.$t('profile.contact-list.first-name').toLowerCase() })
        }
      ],
      lastName: [
        {
          required: true,
          message: this.$t('form-error.enter', { field: this.$t('profile.contact-list.last-name').toLowerCase() })
        }
      ],
      email: [
        {
          validator: this.validateEmail
        }
      ],
      phone: [
        {
          pattern: /^[0-9+\-#\s()]+$/,
          message: this.$t('form-error.phone.incorrect')
        }
      ]
    };

    return {
      formValue: this.convertFormValueToProxy(getDefaultCompanyFormValue()),
      formRules: {
        officialName: [
          {
            required: true,
            message: this.$t('form-error.enter', { field: this.$t('profile.company.official-name').toLowerCase() })
          }
        ],
        registrationNumber: [
          {
            pattern: /^[0-9A-Za-z-.\s]+$/,
            message: this.$t('form-error.registration-number.incorrect')
          }
        ],
        ...addressFormRules,
        legalContact: contactFormRules,
        billingContact: {
          ...addressFormRules,
          ...contactFormRules
        }
      },
      countries: getCountries(),
      sizes: Object.values(CompanySize),
      industries: Object.values(CompanyIndustry),
      isBillingSameAsLegal: false
    };
  },
  created() {
    this.setFormValue(this.value);
    this.isBillingSameAsLegal = isContactInfoEqual(this.formValue.legalContact, this.formValue.billingContact);
  },
  async mounted() {
    await this.$nextTick();
    this.$refs.form.clearValidate();
  },
  methods: {
    handleFormStatusChange(status) {
      this.$emit('status', status);
    },
    handleLegalContactChange() {
      this.formValue.legalContact = updateContactFromCompany(this.formValue.legalContact, this.formValue);

      this.alignBillingContactToLegal();
    },
    alignBillingContactToLegal() {
      if (!this.isBillingSameAsLegal) {
        return;
      }

      this.formValue.billingContact = alignContactInfo(this.formValue.billingContact, this.formValue.legalContact);
    },
    handleCountryChange(companyOrContact) {
      if (this.countryHasStates(companyOrContact.country)) {
        return;
      }

      companyOrContact.state = '';
    },
    countryHasStates(country) {
      return hasStates(country);
    },
    states(country) {
      return country ? getStates(country) : [];
    },
    convertFormValueToProxy(formValue) {
      const self = this;

      return new Proxy(formValue, {
        get(target, prop) {
          return target[prop];
        },
        set(target, prop, value) {
          target[prop] = value;
          self.$emit('input', target);

          return true;
        }
      });
    },
    setFormValue(value) {
      Object.keys(value).forEach((key) => {
        if (!isEmpty(value[key])) {
          this.formValue[key] = value[key];
        }
      });
    },
    validateEmail(_, value, callback) {
      if (!value) {
        callback(new Error(this.$t('form-error.enter', { field: this.$t('profile.company.email').toLowerCase() })));
      } else if (!isEmailValid(value)) {
        callback(new Error(this.$t('form-error.email.invalid')));
      } else {
        callback();
      }
    },
    validateZipCode(_, value, callback) {
      if (value && value.length > 12) {
        callback(new Error(this.$t('form-error.zip-code.too-long')));
      } else {
        callback();
      }
    },
    async validate() {
      try {
        return await this.$refs.form.validate();
      } catch (e) {
        return false;
      }
    },
    clearValidate() {
      this.$refs.form.clearValidate();
    }
  }
};
</script>
